import {
  APPOINTMENT_SCHEDULING_BOUNDS,
  DOCUMENT_TYPES_FOR_SELECTION,
  DOCUMENT_TYPES_FOR_SELECTION_WITH_ESIGN_FLOW,
  DRAFT_ORDER_CLEANUP_DAYS_OPTIONS,
  DRIVER_COMPLIANCE_EXPIRING_SOON_DAYS_MAX,
  DUPLICATE_ORDER_MERGE_WINDOW_HOURS_BOUNDS,
  HOURS_PER_YEAR,
  LINE_HAUL_BUILD_AHEAD_DAYS_MAX,
  QUOTE_AGENT_SETTING_BOUNDS,
} from '../constants';
import {
  COMPANY_CONFIG_ENUM_VALUES,
  NON_TRANSFERABLE_PIECE_STATUSES,
  type CompanySettingKey,
  type DraftOrderCleanupSource,
} from './schema';
import type { SettingDefinition, SettingOption } from './registry.types';
import { QUOTE_CONTRIBUTION_CUTOFF_BOUNDS } from '../quote-contribution-ratio';
import { GENERATED_APPOINTMENT_CUSHION_MAX_MINUTES } from '../stops';

const enumOptionLabel = (value: string): string => {
  const spaced = value.toLowerCase().replaceAll('_', ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const toOptions = (values: readonly string[]): readonly SettingOption[] =>
  values.map((value) => ({ value, label: enumOptionLabel(value) }));

/**
 * `APPOINTMENT_SCHEDULING_BOUNDS` entries name their edges `minimum` and
 * `maximum`; a number control names them `min` and `max`. Adapting keeps the
 * control's limits reading from the same constant the DTO validates against.
 */
const boundsToControl = ({
  minimum,
  maximum,
}: {
  minimum: number;
  maximum: number;
}): { min: number; max: number } => ({ min: minimum, max: maximum });

/**
 * The two pickup e-sign document types the billing required-documents control
 * offers only while `enableDigitalSignatureFlow` is on. Derived from the two
 * curated lists rather than restated, so adding a type to either one here is
 * enough.
 */
const ESIGN_ONLY_DOCUMENT_TYPES =
  DOCUMENT_TYPES_FOR_SELECTION_WITH_ESIGN_FLOW.filter(
    (documentType) => !DOCUMENT_TYPES_FOR_SELECTION.includes(documentType),
  );

/** Card title shared by the two quote contribution ratio cutoffs. */
const QUOTE_CONTRIBUTION_GROUP = 'Quote contribution ratio';

/** Card title shared by the two draft order cleanup settings. */
const DRAFT_ORDER_CLEANUP_GROUP = 'Draft order cleanup';

/** Zero reads as "Disabled" because it turns the cleanup off. */
const DRAFT_ORDER_CLEANUP_DAYS_SELECT_OPTIONS =
  DRAFT_ORDER_CLEANUP_DAYS_OPTIONS.map((days) => ({
    value: String(days),
    label: days === 0 ? 'Disabled' : `${days} days`,
  }));

/**
 * Labels for `DRAFT_ORDER_CLEANUP_SOURCES`, written out because
 * `toOptions` would render the acronyms as "Edi" and "Api".
 */
const DRAFT_ORDER_CLEANUP_SOURCE_OPTIONS = [
  { value: 'EDI', label: 'EDI' },
  { value: 'API', label: 'API' },
  { value: 'CUSTOMER_PORTAL', label: 'Customer portal' },
] as const satisfies ReadonlyArray<{
  value: DraftOrderCleanupSource;
  label: string;
}>;

const SETTINGS_ROUTE = '/settings';
const MANAGEMENT_ROUTE = '/management';
const DISPATCH_ROUTE = '/dispatch';
const WAREHOUSE_SETTINGS_ROUTE = '/warehouse/settings';
/** The internal-tools company configuration table (`internal/frontend`). */
const INTERNAL_ROUTE = '/company-configurations';

const DISPATCH_PAGE = { route: SETTINGS_ROUTE, tab: 'dispatch' } as const;
const ORDERS_PAGE = { route: SETTINGS_ROUTE, tab: 'orders' } as const;
const BILLING_PAGE = { route: MANAGEMENT_ROUTE, tab: 'billing' } as const;
/**
 * The warehouse settings tabs are addressed by numeric index rather than a
 * slug, and the index shifts with the tabs a company can see, so no stable
 * `tab` value exists to record.
 */
const WAREHOUSE_PAGE = { route: WAREHOUSE_SETTINGS_ROUTE } as const;
/**
 * Route defaults live in the dispatch settings dialog, whose tab is local
 * component state and whose only opener is the board toolbar. `/dispatch?tab=`
 * selects a board tab (`routes`, `map`, a saved view) and has no value that
 * reaches this dialog, so no `tab` exists to record.
 */
const ROUTE_DEFAULTS_PAGE = { route: DISPATCH_ROUTE } as const;
const INTERNAL_PAGE = { route: INTERNAL_ROUTE } as const;

const DISPATCH_WRITE = {
  resource: 'SETTINGS_DISPATCH',
  level: 'WRITE',
} as const;
const ORDERS_WRITE = { resource: 'SETTINGS_ORDERS', level: 'WRITE' } as const;
const BILLING_WRITE = { resource: 'COMPANY_BILLING', level: 'WRITE' } as const;
const WAREHOUSE_WRITE = { resource: 'WAREHOUSES', level: 'WRITE' } as const;
const EMAIL_QUOTES_WRITE = {
  resource: 'EMAIL_QUOTES',
  level: 'WRITE',
} as const;
const MASTER = { resource: 'MASTER' } as const;
const QUOTE_AGENT_DEPENDENCY = {
  key: 'quoteAgentEnabled',
  value: true,
  reason: 'Ask Tenet to turn on the quote agent first',
} as const;

/**
 * Every company configuration key, described once.
 *
 * `satisfies Record<CompanySettingKey, SettingDefinition>` is the drift guard:
 * a zod key with no entry here, or an entry for a key the schema no longer
 * declares, fails `tsc` in this package.
 *
 * `surfaces` records where a setting takes effect, not where it is edited. A
 * setting whose effect could not be traced carries the surface of the page it
 * is edited on, or no surface at all when it is edited from internal tools
 * only; either way its description says so.
 */
export const COMPANY_SETTINGS = {
  addOrdersToOpenInvoices: {
    key: 'addOrdersToOpenInvoices',
    label: 'Add orders to open invoices',
    description:
      'Finalized orders join an open invoice for the billing party instead of starting a new one.',
    page: 'billing',
    group: 'Invoices',
    surfaces: ['invoice'],
    control: { type: 'switch' },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  allCapsInOrderPage: {
    key: 'allCapsInOrderPage',
    label: 'All caps on the order page',
    description: 'Shows every text field on the order page in uppercase.',
    page: 'internal',
    group: 'Order page',
    surfaces: ['orders'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  allowDriverToSelectNextStop: {
    key: 'allowDriverToSelectNextStop',
    label: 'Allow drivers to pick the next stop',
    description:
      'Drivers choose the next stop instead of following route order.',
    page: 'internal',
    group: 'Drivers and stops',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  appointmentChangeCutoffHours: {
    key: 'appointmentChangeCutoffHours',
    label: 'Reschedule notice',
    description:
      'How far ahead of an appointment it stops being changeable, in hours.',
    page: 'dispatch',
    group: 'Appointment scheduling',
    surfaces: ['customer'],
    control: {
      type: 'number',
      unit: 'hours',
      ...boundsToControl(
        APPOINTMENT_SCHEDULING_BOUNDS.appointmentChangeCutoffHours,
      ),
      integer: true,
    },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  appointmentSchedulerModel: {
    key: 'appointmentSchedulerModel',
    label: 'Appointment scheduler model',
    description:
      'Whether the reservation system offers capacity by scheduling zone or by scheduling resource.',
    page: 'internal',
    group: 'Appointment scheduling',
    surfaces: ['customer'],
    control: {
      type: 'select',
      options: toOptions(COMPANY_CONFIG_ENUM_VALUES.AppointmentSchedulerModel),
    },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  appointmentSchedulingCutoffDays: {
    key: 'appointmentSchedulingCutoffDays',
    label: 'Booking horizon',
    description: 'How far ahead a customer may book. Zero offers today only.',
    page: 'dispatch',
    group: 'Appointment scheduling',
    surfaces: ['customer'],
    control: {
      type: 'number',
      unit: 'days',
      ...boundsToControl(
        APPOINTMENT_SCHEDULING_BOUNDS.appointmentSchedulingCutoffDays,
      ),
      integer: true,
    },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  appointmentSchedulingLeadingBufferHours: {
    key: 'appointmentSchedulingLeadingBufferHours',
    label: 'Booking notice',
    description:
      'How far ahead of an appointment a customer must book it, counted back from the start of the day being booked.',
    page: 'dispatch',
    group: 'Appointment scheduling',
    surfaces: ['customer'],
    control: {
      type: 'number',
      unit: 'hours',
      ...boundsToControl(
        APPOINTMENT_SCHEDULING_BOUNDS.appointmentSchedulingLeadingBufferHours,
      ),
      integer: true,
    },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  appointmentSchedulingSlotHours: {
    key: 'appointmentSchedulingSlotHours',
    label: 'Appointment slot length',
    description: 'How long one offered appointment window is.',
    page: 'dispatch',
    group: 'Appointment scheduling',
    surfaces: ['customer'],
    control: {
      type: 'number',
      unit: 'hours',
      ...boundsToControl(
        APPOINTMENT_SCHEDULING_BOUNDS.appointmentSchedulingSlotHours,
      ),
      integer: true,
    },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  appointmentSelfRescheduleLimit: {
    key: 'appointmentSelfRescheduleLimit',
    label: 'Customer reschedules allowed',
    description:
      'How many times a customer may move their own appointment from their booking link.',
    page: 'dispatch',
    group: 'Appointment scheduling',
    surfaces: ['customer'],
    control: {
      type: 'number',
      ...boundsToControl(
        APPOINTMENT_SCHEDULING_BOUNDS.appointmentSelfRescheduleLimit,
      ),
      integer: true,
    },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  autoApplyWaitTimeMinutes: {
    key: 'autoApplyWaitTimeMinutes',
    label: 'Auto-apply wait time minutes',
    description: 'Retired flag that no current code path reads.',
    page: 'internal',
    group: 'Legacy',
    surfaces: [],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  autoGroupStops: {
    key: 'autoGroupStops',
    label: 'Automatically group consecutive stops with the same address',
    description:
      'Consecutive stops at one address are shown and completed as one.',
    page: 'dispatch',
    group: 'Drivers and stops',
    surfaces: ['dispatch-board'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  autoMatchEodDocumentsToOrders: {
    key: 'autoMatchEodDocumentsToOrders',
    label: 'Auto-match end-of-day documents to orders',
    description: 'Retired flag that no current code path reads.',
    page: 'internal',
    group: 'Legacy',
    surfaces: [],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  companyOrdinalStart: {
    key: 'companyOrdinalStart',
    label: 'Starting order name number',
    description:
      'The first number the company-ordinal naming scheme counts from.',
    page: 'orders',
    group: 'Order naming',
    surfaces: ['orders'],
    control: { type: 'number', min: 1, integer: true },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  createRecurringOrdersInAdvanceHours: {
    key: 'createRecurringOrdersInAdvanceHours',
    label: 'Create recurring orders in advance',
    description:
      'How many hours before a scheduled day the recurring orders for that day are created.',
    page: 'internal',
    group: 'Orders',
    surfaces: ['orders'],
    control: { type: 'number', unit: 'hours', min: 0, integer: true },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  debounceAccessorialSearch: {
    key: 'debounceAccessorialSearch',
    label: 'Debounce accessorial search',
    description:
      'Waits for a pause in typing before sending an accessorial search request.',
    page: 'internal',
    group: 'Web app',
    surfaces: ['orders'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  debounceContactSearch: {
    key: 'debounceContactSearch',
    label: 'Debounce contact search',
    description:
      'Waits for a pause in typing before sending a contact search request.',
    page: 'internal',
    group: 'Web app',
    surfaces: ['orders'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  defaultDimFactor: {
    key: 'defaultDimFactor',
    label: 'Default dim factor',
    description:
      'The divisor that converts volume to billable weight when an order has no override.',
    page: 'billing',
    group: 'Rates and surcharges',
    surfaces: ['invoice'],
    control: { type: 'number', min: 0, integer: true },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  defaultFuelSettlementPercentageRate: {
    key: 'defaultFuelSettlementPercentageRate',
    label: 'Default fuel settlement rate',
    description:
      'Share of the fuel surcharge paid out to the driver when no rate is set on the settlement.',
    page: 'billing',
    group: 'Rates and surcharges',
    surfaces: ['invoice'],
    control: { type: 'number', unit: '%', min: 0, max: 100 },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  defaultFuelSurcharge: {
    key: 'defaultFuelSurcharge',
    label: 'Default fuel surcharge',
    description:
      'Applied to new orders when the customer has no fuel surcharge of their own.',
    page: 'billing',
    group: 'Rates and surcharges',
    surfaces: ['invoice', 'customer'],
    control: { type: 'number', unit: '%', min: 0 },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
    dependsOn: {
      key: 'defaultFuelSurchargeBillingMethod',
      value: 'PERCENTAGE',
      reason: 'Set the fuel surcharge billing method to percentage first',
      // CSV order import applies this rate with no reference to the billing
      // method (`csv-orders.service.ts` fuelSurcharge fallback), so a value
      // surviving a switch to flat rate would keep being charged.
      clearWhenUnmet: true,
    },
  },
  defaultFuelSurchargeBillingMethod: {
    key: 'defaultFuelSurchargeBillingMethod',
    label: 'Default fuel surcharge billing method',
    description: 'How the fuel surcharge on a new order is calculated.',
    page: 'billing',
    group: 'Rates and surcharges',
    surfaces: ['invoice'],
    control: {
      type: 'select',
      options: toOptions(COMPANY_CONFIG_ENUM_VALUES.FuelBillingMethod),
    },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  defaultGenerateAppointmentEarlyCushionInMinutes: {
    key: 'defaultGenerateAppointmentEarlyCushionInMinutes',
    label: 'Appointment early cushion',
    description:
      'How long before the ETA an appointment generated from a route opens. The ETA is rounded down to the half hour first.',
    page: 'route-defaults',
    group: 'Route defaults',
    surfaces: ['dispatch-board'],
    control: {
      type: 'number',
      unit: 'minutes',
      min: 0,
      max: GENERATED_APPOINTMENT_CUSHION_MAX_MINUTES,
      integer: true,
    },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: ROUTE_DEFAULTS_PAGE,
  },
  defaultGenerateAppointmentDurationInMinutes: {
    key: 'defaultGenerateAppointmentDurationInMinutes',
    label: 'Appointment late cushion',
    description:
      'How long after the ETA an appointment generated from a route closes. The ETA is rounded down to the half hour first.',
    page: 'route-defaults',
    group: 'Route defaults',
    surfaces: ['dispatch-board'],
    control: {
      type: 'number',
      unit: 'minutes',
      min: 0,
      max: GENERATED_APPOINTMENT_CUSHION_MAX_MINUTES,
      integer: true,
    },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: ROUTE_DEFAULTS_PAGE,
  },
  defaultInvoiceDocumentsRequired: {
    key: 'defaultInvoiceDocumentsRequired',
    label: 'Required documents',
    description:
      'Document types an order must carry before it can go onto an invoice.',
    page: 'billing',
    group: 'Invoices',
    surfaces: ['invoice'],
    control: {
      type: 'multi-select',
      options: toOptions(DOCUMENT_TYPES_FOR_SELECTION),
      gatedOptions: {
        key: 'enableDigitalSignatureFlow',
        value: true,
        options: toOptions(ESIGN_ONLY_DOCUMENT_TYPES),
      },
    },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  defaultPalletHeight: {
    key: 'defaultPalletHeight',
    label: 'Default pallet height',
    description: 'Height prefilled on a new pallet when none is measured.',
    page: 'internal',
    group: 'Warehouse',
    surfaces: ['warehouse'],
    control: { type: 'number', unit: 'in', min: 1, integer: true },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  defaultPalletLength: {
    key: 'defaultPalletLength',
    label: 'Default pallet length',
    description: 'Length prefilled on a new pallet when none is measured.',
    page: 'internal',
    group: 'Warehouse',
    surfaces: ['warehouse'],
    control: { type: 'number', unit: 'in', min: 1, integer: true },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  defaultPalletWidth: {
    key: 'defaultPalletWidth',
    label: 'Default pallet width',
    description: 'Width prefilled on a new pallet when none is measured.',
    page: 'internal',
    group: 'Warehouse',
    surfaces: ['warehouse'],
    control: { type: 'number', unit: 'in', min: 1, integer: true },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  defaultRouteLoadTimeInMinutes: {
    key: 'defaultRouteLoadTimeInMinutes',
    label: 'Default load time',
    description: 'Time a new route allows for loading before it departs.',
    page: 'route-defaults',
    group: 'Route defaults',
    surfaces: ['dispatch-board'],
    control: { type: 'number', unit: 'minutes', min: 0 },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: ROUTE_DEFAULTS_PAGE,
  },
  defaultRouteStartTime: {
    key: 'defaultRouteStartTime',
    label: 'Default start time',
    description: 'Time of day a newly created route is planned to start.',
    page: 'route-defaults',
    group: 'Route defaults',
    surfaces: ['dispatch-board'],
    control: { type: 'time' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: ROUTE_DEFAULTS_PAGE,
  },
  defaultRouteUnloadTimeInMinutes: {
    key: 'defaultRouteUnloadTimeInMinutes',
    label: 'Default unload time',
    description: 'Time a new route allows for unloading when it returns.',
    page: 'route-defaults',
    group: 'Route defaults',
    surfaces: ['dispatch-board'],
    control: { type: 'number', unit: 'minutes', min: 0 },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: ROUTE_DEFAULTS_PAGE,
  },
  defaultStandardStopType: {
    key: 'defaultStandardStopType',
    label: 'Default standard stop type',
    description: 'Stop type prefilled on a new standard stop.',
    page: 'internal',
    group: 'Orders',
    surfaces: ['orders'],
    control: {
      type: 'select',
      options: toOptions(COMPANY_CONFIG_ENUM_VALUES.StandardStopType),
    },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  defaultToAccessorial: {
    key: 'defaultToAccessorial',
    label: 'Default custom charges to accessorial',
    description: 'A new custom charge starts as an accessorial charge.',
    page: 'billing',
    group: 'Charges',
    surfaces: ['invoice'],
    control: { type: 'switch' },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  draftOrderCleanupDays: {
    key: 'draftOrderCleanupDays',
    label: 'Delete draft orders after',
    description:
      'Draft orders created and last updated longer ago than this are deleted over the weekend.',
    page: 'orders',
    group: DRAFT_ORDER_CLEANUP_GROUP,
    surfaces: ['orders'],
    control: {
      type: 'select',
      options: DRAFT_ORDER_CLEANUP_DAYS_SELECT_OPTIONS,
      numeric: true,
    },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  draftOrderCleanupSources: {
    key: 'draftOrderCleanupSources',
    label: 'Draft orders to delete',
    description:
      'Which kinds of draft order the weekend cleanup deletes. Partners are not notified.',
    page: 'orders',
    group: DRAFT_ORDER_CLEANUP_GROUP,
    surfaces: ['orders'],
    control: {
      type: 'multi-select',
      options: DRAFT_ORDER_CLEANUP_SOURCE_OPTIONS,
    },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  driverComplianceExpiringSoonDays: {
    key: 'driverComplianceExpiringSoonDays',
    label: 'Expiring soon window',
    description:
      'How many days before its expiry date a driver compliance item shows as expiring soon.',
    page: 'dispatch',
    group: 'Driver compliance',
    surfaces: [],
    control: {
      type: 'number',
      unit: 'days',
      min: 1,
      max: DRIVER_COMPLIANCE_EXPIRING_SOON_DAYS_MAX,
      integer: true,
    },
    // Owned by whoever manages driver compliance, not by dispatch settings.
    permission: { resource: 'COMPANY_DRIVER_COMPLIANCE', level: 'WRITE' },
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  driversShouldMarkFreightAsLoaded: {
    key: 'driversShouldMarkFreightAsLoaded',
    label: 'Require drivers to mark on-hand freight as loaded',
    description: 'Each piece is confirmed loaded before the driver departs.',
    page: 'dispatch',
    group: 'Drivers and stops',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  duplicateOrderMergeWindowHours: {
    key: 'duplicateOrderMergeWindowHours',
    label: 'Merge duplicate orders created within',
    description:
      'An arriving order with the same customer and HAWB as an order created within this many hours merges into it, or is deleted if that order is past Created (for example scheduled, on hold or picked up), so a long window can catch a new shipment that reuses a HAWB. Customers can override this; 0 turns merging off.',
    page: 'orders',
    group: 'Duplicate orders',
    surfaces: ['orders'],
    control: {
      type: 'number',
      unit: 'hours',
      ...boundsToControl(DUPLICATE_ORDER_MERGE_WINDOW_HOURS_BOUNDS),
      integer: true,
    },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  enableConsolidatedOrders: {
    key: 'enableConsolidatedOrders',
    label: 'Enable consolidated orders',
    description: 'Retired flag that no current code path reads.',
    page: 'internal',
    group: 'Legacy',
    surfaces: [],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  enableDigitalSignatureFlow: {
    key: 'enableDigitalSignatureFlow',
    label: 'Offer pickup e-sign documents',
    description:
      'Adds the pickup e-sign document types; delivery e-sign is always available.',
    page: 'internal',
    group: 'Proof of delivery',
    surfaces: ['customer'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  enablePaymentMethod: {
    key: 'enablePaymentMethod',
    label: 'Show the payment method to drivers',
    description:
      'A driver sees the payment method on the stop screen when the order carries one.',
    page: 'internal',
    group: 'Drivers and stops',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  fulfillmentTypeEnabled: {
    key: 'fulfillmentTypeEnabled',
    label: 'Enable fulfillment type',
    description:
      'Adds the fulfillment type to the orders page and to dispatch filtering.',
    page: 'internal',
    group: 'Feature gates',
    surfaces: ['orders', 'dispatch-board'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  getDriverLocationDataFromMobile: {
    key: 'getDriverLocationDataFromMobile',
    label: 'Track driver location from the mobile app',
    description:
      'Positions appear on the dispatch map while a route is active.',
    page: 'dispatch',
    group: 'Drivers and stops',
    surfaces: ['driver-app', 'map'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  includeHyphensInInvoiceName: {
    key: 'includeHyphensInInvoiceName',
    label: 'Include hyphens in the invoice name',
    description: 'Names invoices ABC-INV-123 rather than ABCINV123.',
    page: 'billing',
    group: 'Invoices',
    surfaces: ['invoice'],
    control: { type: 'switch' },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  includeINVInInvoiceName: {
    key: 'includeINVInInvoiceName',
    label: 'Include INV in the invoice name',
    description: 'Names invoices ABC-INV-123 rather than ABC-123.',
    page: 'billing',
    group: 'Invoices',
    surfaces: ['invoice'],
    control: { type: 'switch' },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  invoiceNotes: {
    key: 'invoiceNotes',
    label: 'Invoice notes',
    description:
      'Retired company-wide invoice note, migrated into the catch-all invoice note rule.',
    page: 'internal',
    group: 'Legacy',
    surfaces: [],
    control: { type: 'text', multiline: true },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
    deprecated:
      'Replaced by invoice note rules; read only as the fallback for posted invoices the backfill has not reached',
  },
  invoicePdfLogoUrl: {
    key: 'invoicePdfLogoUrl',
    label: 'Invoice PDF logo',
    description: 'Image printed in the header of the invoice PDF.',
    page: 'internal',
    group: 'Invoicing',
    surfaces: ['invoice', 'customer'],
    control: { type: 'url' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  lineHaulBuildAheadDays: {
    key: 'lineHaulBuildAheadDays',
    label: 'Line haul build-ahead horizon',
    description:
      'How far ahead the recurring generator materializes every line-haul lane, unless a lane overrides it. No web control writes it today; the linehaul schedule screen only reads it as the per-lane placeholder.',
    page: 'internal',
    group: 'Line haul scheduling',
    surfaces: ['dispatch-board'],
    control: {
      type: 'number',
      unit: 'days',
      min: 0,
      max: LINE_HAUL_BUILD_AHEAD_DAYS_MAX,
      integer: true,
    },
    permission: { resource: 'COMPANY_LINE_HAUL', level: 'WRITE' },
    exposedToUser: false,
    location: INTERNAL_PAGE,
    dependsOn: {
      key: 'lineHaulEnabled',
      value: true,
      reason: 'Turn on line haul first',
    },
  },
  lineHaulEnabled: {
    key: 'lineHaulEnabled',
    label: 'Enable line haul',
    description:
      'Turns on line haul manifests, the line haul board and the driver app line haul screens.',
    page: 'internal',
    group: 'Feature gates',
    surfaces: ['driver-app', 'dispatch-board'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  lineHaulMobileFutureWindowHours: {
    key: 'lineHaulMobileFutureWindowHours',
    label: 'Line haul driver future visibility',
    description: 'How far ahead upcoming loads appear.',
    page: 'dispatch',
    group: 'Line haul driver visibility',
    surfaces: ['driver-app'],
    control: {
      type: 'number',
      unit: 'hours',
      min: 1,
      max: HOURS_PER_YEAR,
      integer: true,
    },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
    dependsOn: {
      key: 'lineHaulEnabled',
      value: true,
      reason: 'Turn on line haul first',
    },
  },
  lineHaulMobilePastWindowHours: {
    key: 'lineHaulMobilePastWindowHours',
    label: 'Line haul driver past visibility',
    description: 'How far back line haul drivers can see completed work.',
    page: 'dispatch',
    group: 'Line haul driver visibility',
    surfaces: ['driver-app'],
    control: {
      type: 'number',
      unit: 'hours',
      min: 1,
      max: HOURS_PER_YEAR,
      integer: true,
    },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
    dependsOn: {
      key: 'lineHaulEnabled',
      value: true,
      reason: 'Turn on line haul first',
    },
  },
  lineHaulPlanningEnabled: {
    key: 'lineHaulPlanningEnabled',
    label: 'Show Linehaul Planning',
    description:
      'Shows or hides the Linehaul Planning board in the Dispatch menu. It does not restrict line haul data.',
    page: 'dispatch',
    group: 'Line haul planning',
    surfaces: ['dispatch-board'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
    dependsOn: {
      key: 'lineHaulEnabled',
      value: true,
      reason: 'Turn on line haul first',
    },
  },
  logisticsSectionEnabled: {
    key: 'logisticsSectionEnabled',
    label: 'Show the logistics section on orders',
    description: 'Adds the logistics section, including hazmat, to the order.',
    page: 'orders',
    group: 'Order form',
    surfaces: ['orders'],
    control: { type: 'switch' },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  markOnHandUponPickup: {
    key: 'markOnHandUponPickup',
    label: 'Mark pieces as on hand upon pickup scan',
    description:
      'A pickup scan puts the piece on hand without a warehouse scan.',
    page: 'dispatch',
    group: 'Drivers and stops',
    surfaces: ['warehouse'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  minDigitsInCompanyOrdinalOrderName: {
    key: 'minDigitsInCompanyOrdinalOrderName',
    label: 'Minimum order name length',
    description:
      'Pads a company-ordinal order name with leading zeros to this length.',
    page: 'orders',
    group: 'Order naming',
    surfaces: ['orders'],
    control: { type: 'number', min: 1, integer: true },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  mobilePollingIntervalInSeconds: {
    key: 'mobilePollingIntervalInSeconds',
    label: 'Driver location polling interval',
    description: 'How often the driver app reports its position.',
    page: 'internal',
    group: 'Drivers and stops',
    surfaces: ['driver-app', 'map'],
    control: { type: 'number', unit: 'seconds', min: 1, integer: true },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  notificationsEnabled: {
    key: 'notificationsEnabled',
    label: 'Enable in-app notifications',
    description:
      'Turns on the bell menu covering email sends and attempted stops.',
    page: 'internal',
    group: 'Notifications and emails',
    surfaces: ['dispatch-board'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  orderIdentifierForCoverSheet: {
    key: 'orderIdentifierForCoverSheet',
    label: 'Order identifier for cover sheet',
    description: 'Which order number the cover sheet barcode encodes.',
    page: 'orders',
    group: 'Documents',
    surfaces: ['orders'],
    control: {
      type: 'select',
      options: toOptions(COMPANY_CONFIG_ENUM_VALUES.OrderIdentifier),
    },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  orderLevelOsdEnabled: {
    key: 'orderLevelOsdEnabled',
    label: 'Enable order-level OS&D',
    description:
      'Overage, shortage and damage are recorded against the order rather than only against individual pieces.',
    page: 'dispatch',
    group: 'Drivers and stops',
    surfaces: ['orders'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  orderNamingScheme: {
    key: 'orderNamingScheme',
    label: 'Order naming scheme',
    description:
      'Whether an order name counts per contact or per company, for example EXPE-123 or 000123.',
    page: 'orders',
    group: 'Order naming',
    surfaces: ['orders'],
    control: {
      type: 'select',
      options: toOptions(COMPANY_CONFIG_ENUM_VALUES.OrderNamingScheme),
    },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  packageWarehouseLocationsEnabled: {
    key: 'packageWarehouseLocationsEnabled',
    label: 'Enable warehouse locations on pieces',
    description:
      'Lets a warehouse assign a storage location to each received piece.',
    page: 'internal',
    group: 'Warehouse',
    surfaces: ['warehouse'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  pieceTransferableStatuses: {
    key: 'pieceTransferableStatuses',
    label: 'Transferable piece statuses',
    description:
      'Which piece statuses may be moved onto a transfer; terminal statuses are refused.',
    page: 'internal',
    group: 'Warehouse',
    surfaces: ['warehouse'],
    control: {
      type: 'multi-select',
      options: toOptions(
        COMPANY_CONFIG_ENUM_VALUES.PieceStatus.filter(
          (status) => !NON_TRANSFERABLE_PIECE_STATUSES.includes(status),
        ),
      ),
    },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  pointToPointTariffsEnabled: {
    key: 'pointToPointTariffsEnabled',
    label: 'Enable point-to-point tariffs',
    description: 'Rates orders from a tariff between two named points.',
    page: 'internal',
    group: 'Feature gates',
    surfaces: ['invoice'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  quoteAgentEnabled: {
    key: 'quoteAgentEnabled',
    label: 'Quote agent',
    description:
      "Reads quote requests from the company's connected mailboxes and prepares a priced draft and a reply for each.",
    page: 'internal',
    group: 'Quote agent',
    surfaces: ['email', 'orders'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    staffOnly: true,
    location: INTERNAL_PAGE,
  },
  quoteAgentDefaultMode: {
    key: 'quoteAgentDefaultMode',
    label: 'Reply policy',
    description:
      "Whether replies wait for review, send on their own below a price ceiling, or always send. A customer's own policy overrides it.",
    page: 'orders',
    group: 'Quote agent',
    surfaces: ['email', 'orders'],
    control: {
      type: 'select',
      options: [
        { value: 'REVIEW_ALL', label: 'Review every reply' },
        {
          value: 'AUTO_SEND_BELOW',
          label: 'Send on its own below the ceiling',
        },
        { value: 'AUTO_SEND', label: 'Send on its own' },
      ],
    },
    permission: EMAIL_QUOTES_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
    dependsOn: QUOTE_AGENT_DEPENDENCY,
  },
  quoteAgentAutoSendMaxCents: {
    key: 'quoteAgentAutoSendMaxCents',
    label: 'Auto-send ceiling',
    description:
      'Under "Send on its own below the ceiling", replies quoting more than this, in cents, wait for review. Customers on that policy without a ceiling of their own use this one. 50000 is $500.00.',
    page: 'orders',
    group: 'Quote agent',
    surfaces: ['email', 'orders'],
    control: {
      type: 'number',
      unit: 'cents',
      ...boundsToControl(QUOTE_AGENT_SETTING_BOUNDS.quoteAgentAutoSendMaxCents),
      integer: true,
    },
    permission: EMAIL_QUOTES_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
    dependsOn: QUOTE_AGENT_DEPENDENCY,
  },
  quoteAgentMinConfidence: {
    key: 'quoteAgentMinConfidence',
    label: 'Minimum confidence to send',
    description:
      'A reply below this extraction confidence waits for review whatever the policy says.',
    page: 'orders',
    group: 'Quote agent',
    surfaces: ['email', 'orders'],
    control: {
      type: 'number',
      unit: '%',
      ...boundsToControl(QUOTE_AGENT_SETTING_BOUNDS.quoteAgentMinConfidence),
      integer: true,
    },
    permission: EMAIL_QUOTES_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
    dependsOn: QUOTE_AGENT_DEPENDENCY,
  },
  quoteAgentValidityHours: {
    key: 'quoteAgentValidityHours',
    label: 'Quote validity',
    description: 'How long a quoted price holds before the quote expires.',
    page: 'orders',
    group: 'Quote agent',
    surfaces: ['email', 'orders'],
    control: {
      type: 'number',
      unit: 'hours',
      ...boundsToControl(QUOTE_AGENT_SETTING_BOUNDS.quoteAgentValidityHours),
      integer: true,
    },
    permission: EMAIL_QUOTES_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
    dependsOn: QUOTE_AGENT_DEPENDENCY,
  },
  quoteAgentDailyModelBudgetCents: {
    key: 'quoteAgentDailyModelBudgetCents',
    label: 'Daily model budget',
    description:
      'Model spend allowed per day, in cents; requests over it go to manual handling.',
    page: 'internal',
    group: 'Quote agent',
    surfaces: ['email', 'orders'],
    control: {
      type: 'number',
      unit: 'cents',
      ...boundsToControl(
        QUOTE_AGENT_SETTING_BOUNDS.quoteAgentDailyModelBudgetCents,
      ),
      integer: true,
    },
    permission: MASTER,
    exposedToUser: false,
    staffOnly: true,
    location: INTERNAL_PAGE,
    dependsOn: QUOTE_AGENT_DEPENDENCY,
  },
  recoveryTariffsEnabled: {
    key: 'recoveryTariffsEnabled',
    label: 'Enable recovery tariffs',
    description:
      'Rates recovery stops from a tariff; it is refused while auto-apply accessorial rules for recovery stops still exist.',
    page: 'internal',
    group: 'Feature gates',
    surfaces: ['invoice'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  requireDimsToFinalizeCharges: {
    key: 'requireDimsToFinalizeCharges',
    label: 'Require package dims to finalize charges',
    description:
      'Charges cannot be finalized until every package carries dimensions.',
    page: 'orders',
    group: 'Order form',
    surfaces: ['orders', 'invoice'],
    control: { type: 'switch' },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  requireDriverSignature: {
    key: 'requireDriverSignature',
    label: 'Require a signature from the driver',
    description:
      'The driver signs as well as the consignee before a delivery closes. No reader has been traced, so no surface is claimed.',
    page: 'internal',
    group: 'Proof of delivery',
    surfaces: [],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  requireDriverToAcknowledgeRouteChange: {
    key: 'requireDriverToAcknowledgeRouteChange',
    label: 'Require drivers to acknowledge route changes',
    description:
      'A driver taps to accept a changed route before the stop list updates.',
    page: 'dispatch',
    group: 'Drivers and stops',
    surfaces: ['driver-app', 'dispatch-board'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  requireDriverToConfirmStopCompletedAtTime: {
    key: 'requireDriverToConfirmStopCompletedAtTime',
    label: 'Have drivers confirm the timestamp when completing a stop',
    description:
      'Drivers can correct the completion time before the stop closes.',
    page: 'dispatch',
    group: 'Drivers and stops',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  requirePODPhotoAndName: {
    key: 'requirePODPhotoAndName',
    label: 'Require a photo and signee name at delivery stops',
    description:
      'The combined delivery requirement, kept for companies still on the unsplit control.',
    page: 'dispatch',
    group: 'Proof of delivery',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
    deprecated:
      'Replaced by requirePODPhotoForDeliveries and requireSigneeNameForDeliveries',
  },
  requirePODPhotoAndNameForPickups: {
    key: 'requirePODPhotoAndNameForPickups',
    label: 'Require a photo and signee name at pickup stops',
    description:
      'The combined pickup requirement, kept for companies still on the unsplit control.',
    page: 'dispatch',
    group: 'Proof of delivery',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
    deprecated:
      'Replaced by requirePODPhotoForPickups and requireSigneeNameForPickups',
  },
  requirePODPhotoForDeliveries: {
    key: 'requirePODPhotoForDeliveries',
    label: 'Require photo at delivery stops',
    description: 'A signed receipt counts in place of the photo.',
    page: 'dispatch',
    group: 'Proof of delivery',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  requireOnHandBeforeSelfScheduling: {
    key: 'requireOnHandBeforeSelfScheduling',
    label: 'Wait for freight on hand before customers book',
    description:
      'Customers can book a delivery only once the order is marked on hand. Until then the booking page explains why no times are offered. Pickups are not affected.',
    page: 'dispatch',
    group: 'Appointment scheduling',
    surfaces: ['customer'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  requirePODPhotoForPickups: {
    key: 'requirePODPhotoForPickups',
    label: 'Require photo at pickup stops',
    description: 'A signed receipt counts in place of the photo.',
    page: 'dispatch',
    group: 'Proof of delivery',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  requireReviewForEdiOrderCreation: {
    key: 'requireReviewForEdiOrderCreation',
    label: 'Review EDI orders before creating them',
    description:
      'Inbound EDI orders wait in review instead of being accepted automatically.',
    page: 'internal',
    group: 'Orders',
    surfaces: ['orders'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  requireSignedPodToCompleteStop: {
    key: 'requireSignedPodToCompleteStop',
    label: 'Require a signed proof of delivery to complete a stop',
    description:
      'A delivery completed from the driver app stays open until its proof of delivery has been signed.',
    page: 'internal',
    group: 'Proof of delivery',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  requireSigneeNameForDeliveries: {
    key: 'requireSigneeNameForDeliveries',
    label: 'Require signee name at delivery stops',
    description: 'The name is captured on the signature screen.',
    page: 'dispatch',
    group: 'Proof of delivery',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  requireSigneeNameForPickups: {
    key: 'requireSigneeNameForPickups',
    label: 'Require signee name at pickup stops',
    description: 'The name is captured on the signature screen.',
    page: 'dispatch',
    group: 'Proof of delivery',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  restrictEditingOrderName: {
    key: 'restrictEditingOrderName',
    label: 'Make order name un-editable',
    description: 'The generated order name cannot be changed by hand.',
    page: 'orders',
    group: 'Order form',
    surfaces: ['orders'],
    control: { type: 'switch' },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  reviewUrl: {
    key: 'reviewUrl',
    label: 'Customer review link',
    description:
      'Link offered in delivery-completion emails once the email template references it.',
    page: 'internal',
    group: 'Notifications and emails',
    surfaces: ['email', 'customer'],
    control: { type: 'url' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  sameDayDispatchCellHeader: {
    key: 'sameDayDispatchCellHeader',
    label: 'Same-day dispatch cell header',
    description: 'Which value titles a card on the same-day dispatch board.',
    page: 'internal',
    group: 'Dispatch board',
    surfaces: ['dispatch-board'],
    control: {
      type: 'select',
      options: toOptions(COMPANY_CONFIG_ENUM_VALUES.SameDayDispatchCellHeader),
    },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  sendSignedPodEmailAfterLateSignature: {
    key: 'sendSignedPodEmailAfterLateSignature',
    label: 'Email the proof of delivery signed after the stop closed',
    description:
      'Sends the delivery contact the signed proof of delivery, at most once per stop.',
    page: 'internal',
    group: 'Notifications and emails',
    surfaces: ['email', 'customer'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  sendSummaryEmailWithIncompleteRoutes: {
    key: 'sendSummaryEmailWithIncompleteRoutes',
    label: 'Send the end-of-day summary with incomplete routes',
    description:
      'The end-of-day summary email goes out even when routes are still open. `User` carries a column of the same name, and the only web control for that name edits the user one, not this.',
    page: 'internal',
    group: 'Notifications and emails',
    surfaces: ['email'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  serviceLevelOptional: {
    key: 'serviceLevelOptional',
    label: 'Make service level optional',
    description: 'An order can be saved without a service level.',
    page: 'internal',
    group: 'Orders',
    surfaces: ['orders'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  showAccessorialsToDrivers: {
    key: 'showAccessorialsToDrivers',
    label: 'Show accessorials to drivers',
    description: 'Adds an Accessorials row to every stop. View only.',
    page: 'dispatch',
    group: 'Drivers and stops',
    surfaces: ['driver-app'],
    control: { type: 'switch' },
    permission: DISPATCH_WRITE,
    exposedToUser: true,
    location: DISPATCH_PAGE,
    driverPreview: 'showAccessorialsToDrivers',
  },
  showChargesOnCustomerPortal: {
    key: 'showChargesOnCustomerPortal',
    label: 'Show charges on the customer portal',
    description: 'Retired flag that no current code path reads.',
    page: 'internal',
    group: 'Legacy',
    surfaces: [],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
    deprecated: 'Marked deprecated in schema.prisma; no reader remains',
  },
  showCompanyNameOnBillOfLadingDoc: {
    key: 'showCompanyNameOnBillOfLadingDoc',
    label: 'Show company name and logo on the bill of lading',
    description:
      'Prints the company name and logo in the bill of lading header.',
    page: 'orders',
    group: 'Documents',
    surfaces: ['orders', 'customer'],
    control: { type: 'switch' },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  showDriverSettlementInBilling: {
    key: 'showDriverSettlementInBilling',
    label: 'Show driver settlement in billing',
    description:
      'Adds the driver settlement section to the billing review screen.',
    page: 'billing',
    group: 'Charges',
    surfaces: ['invoice'],
    control: { type: 'switch' },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  showHelpersColumnInDispatch: {
    key: 'showHelpersColumnInDispatch',
    label: 'Show the helpers column in dispatch',
    description: 'Retired flag that no current code path reads.',
    page: 'internal',
    group: 'Legacy',
    surfaces: [],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  showKilogramsAndCentimeters: {
    key: 'showKilogramsAndCentimeters',
    label: 'Show kilograms and centimeters',
    description:
      'Weights read in kilograms and dimensions in centimeters across the web app.',
    page: 'internal',
    group: 'Web app',
    surfaces: ['orders', 'invoice'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  showManuallyConfirmedByDefault: {
    key: 'showManuallyConfirmedByDefault',
    label: 'Show manually confirmed by default',
    description: 'Retired flag that no current code path reads.',
    page: 'internal',
    group: 'Legacy',
    surfaces: [],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  showVehicleInDailyDispatch: {
    key: 'showVehicleInDailyDispatch',
    label: 'Show the vehicle in daily dispatch',
    description: 'Adds the assigned vehicle to each daily dispatch row.',
    page: 'internal',
    group: 'Dispatch board',
    surfaces: ['dispatch-board'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  startingInvoiceOrdinalNumber: {
    key: 'startingInvoiceOrdinalNumber',
    label: 'Starting journal number',
    description: 'The first number invoice journal numbers count from.',
    page: 'billing',
    group: 'Invoices',
    surfaces: ['invoice'],
    control: { type: 'number', min: 1, integer: true },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  t2tDelegationEnabled: {
    key: 't2tDelegationEnabled',
    label: 'Enable T2T delegation',
    description:
      'Master gate. Both companies must enable it for the delegation link to activate.',
    page: 'dispatch',
    group: 'Tenant-to-tenant',
    surfaces: ['dispatch-board'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: true,
    location: DISPATCH_PAGE,
  },
  t2tDocumentPropagationAllowlist: {
    key: 't2tDocumentPropagationAllowlist',
    label: 'T2T document propagation allowlist',
    description:
      'Which document types may cross the tenant boundary; leaving it empty falls back to the operational-document default rather than blocking everything.',
    page: 'internal',
    group: 'Tenant-to-tenant',
    surfaces: ['dispatch-board'],
    control: {
      type: 'multi-select',
      options: toOptions(COMPANY_CONFIG_ENUM_VALUES.DocumentType),
    },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  t2tSyncEnabled: {
    key: 't2tSyncEnabled',
    label: 'Enable T2T sync',
    description:
      'Mirrors order events across the delegation link when delegation is active.',
    page: 'dispatch',
    group: 'Tenant-to-tenant',
    surfaces: ['dispatch-board'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: true,
    location: DISPATCH_PAGE,
    dependsOn: {
      key: 't2tDelegationEnabled',
      value: true,
      reason: 'Turn on T2T delegation first',
    },
  },
  textNextCustomerForAppointments: {
    key: 'textNextCustomerForAppointments',
    label: 'Text the next customer on the route',
    description:
      'Sends the contact for the next stop a message as the driver approaches.',
    page: 'internal',
    group: 'Notifications and emails',
    surfaces: ['customer'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  useDriverNumberForDispatch: {
    key: 'useDriverNumberForDispatch',
    label: 'Use the driver reference number in dispatch',
    description:
      'Shows the driver reference number when picking a driver for a route.',
    page: 'internal',
    group: 'Dispatch board',
    surfaces: ['dispatch-board'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  quoteContributionMarginalFromRatio: {
    key: 'quoteContributionMarginalFromRatio',
    label: 'Marginal from',
    description:
      "The contribution ratio at which a quote stops showing as Not profitable and shows as Marginal. The ratio is the quote's revenue less driver pay, as a percentage of what your tariffs charge for it.",
    page: 'orders',
    group: QUOTE_CONTRIBUTION_GROUP,
    surfaces: ['orders'],
    control: {
      type: 'number',
      ...boundsToControl(QUOTE_CONTRIBUTION_CUTOFF_BOUNDS),
    },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  quoteContributionProfitableFromRatio: {
    key: 'quoteContributionProfitableFromRatio',
    label: 'Profitable from',
    description:
      'The contribution ratio at which a quote shows as Profitable. Set it at or above "Marginal from".',
    page: 'orders',
    group: QUOTE_CONTRIBUTION_GROUP,
    surfaces: ['orders'],
    control: {
      type: 'number',
      ...boundsToControl(QUOTE_CONTRIBUTION_CUTOFF_BOUNDS),
    },
    permission: ORDERS_WRITE,
    exposedToUser: true,
    location: ORDERS_PAGE,
  },
  useHawbForLotLabelBarcode: {
    key: 'useHawbForLotLabelBarcode',
    label: 'Use the HAWB on lot label barcodes',
    description:
      'Prints lot label barcodes using the HAWB instead of the order number. Only enable when HAWBs are unique per order for this company.',
    page: 'warehouse',
    group: 'Warehouse scanning',
    surfaces: ['warehouse'],
    control: { type: 'switch' },
    permission: WAREHOUSE_WRITE,
    exposedToUser: true,
    location: WAREHOUSE_PAGE,
  },
  useJournalNumberForInvoice: {
    key: 'useJournalNumberForInvoice',
    label: 'Invoice identifier',
    description:
      'Identifies an invoice by its journal number rather than its invoice name.',
    page: 'billing',
    group: 'Invoices',
    surfaces: ['invoice'],
    control: { type: 'switch' },
    permission: BILLING_WRITE,
    exposedToUser: true,
    location: BILLING_PAGE,
  },
  useSeparateDigitalSignatureStep: {
    key: 'useSeparateDigitalSignatureStep',
    label: 'Collect the digital signature in its own step',
    description:
      'Splits signature capture out of the completion screen. No reader has been traced, so no surface is claimed.',
    page: 'internal',
    group: 'Proof of delivery',
    surfaces: [],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  wmsAutoMarkOnHand: {
    key: 'wmsAutoMarkOnHand',
    label: 'Automatically mark containers and items on hand',
    description:
      'A received container or item is put on hand without a separate scan.',
    page: 'warehouse',
    group: 'Warehouse scanning',
    surfaces: ['warehouse'],
    control: { type: 'switch' },
    permission: WAREHOUSE_WRITE,
    exposedToUser: true,
    location: WAREHOUSE_PAGE,
  },
  wmsEnabled: {
    key: 'wmsEnabled',
    label: 'Enable the warehouse management system',
    description:
      'Turns on the warehouse module and the warehouse scanning screens.',
    page: 'internal',
    group: 'Feature gates',
    surfaces: ['driver-app', 'warehouse'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  wmsShortTermStorageEnabled: {
    key: 'wmsShortTermStorageEnabled',
    label: 'Enable short-term storage',
    description: 'Adds short-term storage handling to the warehouse module.',
    page: 'internal',
    group: 'Feature gates',
    surfaces: ['warehouse'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
  wmsStorageOrdersEnabled: {
    key: 'wmsStorageOrdersEnabled',
    label: 'Enable warehouse receipts',
    description: 'Adds storage orders and the items table to the warehouse.',
    page: 'internal',
    group: 'Feature gates',
    surfaces: ['warehouse'],
    control: { type: 'switch' },
    permission: MASTER,
    exposedToUser: false,
    location: INTERNAL_PAGE,
  },
} satisfies Record<CompanySettingKey, SettingDefinition>;
